import { localStorage } from "@prismicio/editor-support/DOM"
import { parseJSON } from "@prismicio/editor-support/String"
import { Box } from "@prismicio/editor-ui"
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import { z } from "zod"

import { closedBanners as closedBannersKey } from "../constants"

type BannerItem = {
  id: string
  render: (bannerProps: { onClose: () => void }) => React.ReactNode
}

type BannerContext = {
  showBanner: (banner: BannerItem) => void
  bannerHeight: number
  banner: BannerItem | undefined
  closeBanner: (id: string) => void
  resetBannerId: (id: string) => void
  setBannerHeight: (height: number) => void
}

const BannerContext = createContext<BannerContext | undefined>(undefined)

interface BannerProviderProps {
  children: React.ReactNode
}

export const BannerProvider = ({ children }: BannerProviderProps) => {
  const [bannerHeight, setBannerHeight] = useState(0)
  const [banners, setBanners] = useState<BannerItem[]>([])
  const closedBanners = useClosedBanners()

  const showBanner = (newBanner: BannerItem) => {
    setBanners((currentBanners) => {
      const isDuplicate = currentBanners.some(
        (banner) => banner.id === newBanner.id,
      )

      if (isDuplicate) {
        return currentBanners
      }

      return [...currentBanners, newBanner]
    })
  }

  const closeBanner = (id: string) => {
    const newClosedBanners = { ...closedBanners, [id]: Date.now() }
    setClosedBanners(newClosedBanners)
    setBanners((currentBanners) =>
      currentBanners.filter((banner) => banner.id !== id),
    )
  }

  // Reset the banner id so it can be shown again. For example, when the
  // user changes plans, it will track the usage again.
  const resetBannerId = (id: string) => {
    const newClosedBanners = { ...closedBanners }
    delete newClosedBanners[id]
    setClosedBanners(newClosedBanners)
  }

  const openedBanners = banners.filter((banner) => !closedBanners[banner.id])

  const banner = openedBanners[0]

  return (
    <BannerContext.Provider
      value={{
        showBanner,
        bannerHeight,
        banner,
        closeBanner,
        resetBannerId,
        setBannerHeight,
      }}
    >
      {children}
    </BannerContext.Provider>
  )
}

const closedBannersSchema = z.record(z.number()) // timestamps when banner was closed by banner id

type ClosedBanners = z.infer<typeof closedBannersSchema>

function useClosedBanners() {
  const closedBannersStr = useSyncExternalStore(localStorage.subscribe, () =>
    localStorage.read(closedBannersKey),
  )

  return useMemo(
    () => parseJSON(closedBannersStr, closedBannersSchema) ?? {},
    [closedBannersStr],
  )
}

function setClosedBanners(closedBanners: ClosedBanners) {
  localStorage.write(closedBannersKey, JSON.stringify(closedBanners))
}

export const useGlobalBanner = (): BannerContext => {
  const context = useContext(BannerContext)
  if (!context) {
    throw new Error("useGlobalBanner must be used within a BannerProvider")
  }
  return context
}

export function GlobalBanner() {
  const bannerRef = useRef<HTMLDivElement | null>(null)
  const { banner, setBannerHeight, closeBanner } = useGlobalBanner()

  useLayoutEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.offsetHeight)
      }
    }

    updateBannerHeight()

    window.addEventListener("resize", updateBannerHeight)
    return () => {
      window.removeEventListener("resize", updateBannerHeight)
    }
  }, [banner, setBannerHeight])

  return (
    <Box
      ref={bannerRef}
      position="fixed"
      top={0}
      left={0}
      right={0}
      flexDirection="column"
    >
      {banner && banner.render({ onClose: () => closeBanner(banner.id) })}
    </Box>
  )
}
